import React, { useState, useEffect, useContext, useCallback } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  RefreshCw,
  Search,
  RotateCcw,
  Eye,
  FileSpreadsheet,
  CheckCircle2,
  XCircle,
  Clock,
  Send,
  Users,
  CheckCheck
} from "lucide-react";
import { format, subDays, startOfMonth } from "date-fns";
import * as XLSX from "xlsx";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import KpiCards from "./KpiCards";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2.5),
    overflowY: "auto",
    borderRadius: "12px",
    boxShadow: theme.mode === "light"
      ? "0 2px 12px rgba(0,0,0,0.06)"
      : "0 2px 12px rgba(0,0,0,0.3)"
  },
  filterPaper: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2.5),
    borderRadius: "10px",
    backgroundColor: theme.mode === "light" ? "#f9fafb" : "#1f2937",
    border: `1px solid ${theme.palette.divider}`
  },
  tableContainer: {
    marginTop: theme.spacing(1.5),
    borderRadius: "8px",
    border: `1px solid ${theme.palette.divider}`
  },
  tableHeader: {
    fontWeight: 600,
    backgroundColor: theme.mode === "light" ? "#f3f4f6" : "#374151"
  },
  exportButton: {
    backgroundColor: "#16A34A",
    color: "#fff",
    fontWeight: 600,
    "&:hover": {
      backgroundColor: "#15803D"
    }
  },
  tabsContainer: {
    marginTop: theme.spacing(2.5),
    borderBottom: `1px solid ${theme.palette.divider}`
  },
  dialogMessage: {
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    backgroundColor: theme.mode === "light" ? "#f3f4f6" : "#1f2937",
    padding: theme.spacing(2),
    borderRadius: "8px",
    fontSize: "0.9rem",
    marginTop: theme.spacing(1)
  }
}));

const CampaignReports = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const isAdmin = user?.profile === "admin";

  // Filtros
  const todayStr = format(new Date(), "yyyy-MM-dd");
  const thirtyDaysAgoStr = format(subDays(new Date(), 30), "yyyy-MM-dd");

  const [startDate, setStartDate] = useState(thirtyDaysAgoStr);
  const [endDate, setEndDate] = useState(todayStr);
  const [selectedUserId, setSelectedUserId] = useState(isAdmin ? "" : String(user?.id || ""));
  const [selectedStatus, setSelectedStatus] = useState("");
  const [searchParam, setSearchParam] = useState("");

  // Dados
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  const [collaboratorsList, setCollaboratorsList] = useState([]);
  const [usersDropdown, setUsersDropdown] = useState([]);

  const [totals, setTotals] = useState({
    totalCampaigns: 0,
    totalShipping: 0,
    totalSent: 0,
    totalDelivered: 0,
    totalConfirmed: 0,
    totalFailed: 0,
    totalPending: 0,
    deliveryRate: 0
  });

  const [detailRows, setDetailRows] = useState([]);
  const [detailCount, setDetailCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Modal de preview
  const [previewItem, setPreviewItem] = useState(null);

  // Carregar lista de usuários (para o dropdown do Admin)
  useEffect(() => {
    if (isAdmin) {
      api.get("/users/list")
        .then(({ data }) => {
          if (Array.isArray(data)) {
            setUsersDropdown(data);
          }
        })
        .catch(() => {});
    }
  }, [isAdmin]);

  // Carregar resumo consolidado
  const loadSummary = useCallback(async () => {
    try {
      const { data } = await api.get("/campaigns/reports/summary", {
        params: {
          startDate,
          endDate,
          userId: selectedUserId || undefined
        }
      });
      setCollaboratorsList(data.collaborators || []);
      setTotals(data.totals || {});
    } catch (err) {
      toastError(err);
    }
  }, [startDate, endDate, selectedUserId]);

  // Carregar detalhes paginados
  const loadDetails = useCallback(async (targetPage = page, targetLimit = rowsPerPage) => {
    try {
      const { data } = await api.get("/campaigns/reports/detail", {
        params: {
          startDate,
          endDate,
          userId: selectedUserId || undefined,
          status: selectedStatus || undefined,
          searchParam: searchParam || undefined,
          pageNumber: targetPage + 1,
          pageSize: targetLimit
        }
      });
      setDetailRows(data.rows || []);
      setDetailCount(data.count || 0);
    } catch (err) {
      toastError(err);
    }
  }, [startDate, endDate, selectedUserId, selectedStatus, searchParam, page, rowsPerPage]);

  const loadAllData = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([loadSummary(), loadDetails(0, rowsPerPage)]);
      setPage(0);
    } finally {
      setLoading(false);
    }
  }, [loadSummary, loadDetails, rowsPerPage]);

  useEffect(() => {
    loadAllData();
  }, [selectedUserId]);

  const handleFilter = (e) => {
    if (e) e.preventDefault();
    loadAllData();
  };

  const handleClearFilters = () => {
    setStartDate(thirtyDaysAgoStr);
    setEndDate(todayStr);
    setSelectedUserId(isAdmin ? "" : String(user?.id || ""));
    setSelectedStatus("");
    setSearchParam("");
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    loadDetails(newPage, rowsPerPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    setRowsPerPage(newLimit);
    setPage(0);
    loadDetails(0, newLimit);
  };

  // Exportar Excel Completo
  const handleExportExcel = async () => {
    setExporting(true);
    try {
      // Formatar YYYY-MM-DD para DD/MM/AAAA
      const formatDatePtBr = (dateStr) => {
        if (!dateStr) return "-";
        const parts = dateStr.split("-");
        if (parts.length === 3) {
          return `${parts[2]}/${parts[1]}/${parts[0]}`;
        }
        return dateStr;
      };

      const startPt = formatDatePtBr(startDate);
      const endPt = formatDatePtBr(endDate);

      // 1. Obter dados completos dos detalhes para exportação
      const { data: detailData } = await api.get("/campaigns/reports/detail", {
        params: {
          startDate,
          endDate,
          userId: selectedUserId || undefined,
          status: selectedStatus || undefined,
          searchParam: searchParam || undefined,
          isExport: true
        }
      });

      const wb = XLSX.utils.book_new();

      // Aba 1: Desempenho por Colaborador
      const summarySheetData = [
        ["RELATÓRIO DE DISPAROS EM MASSA - CONSOLIDADO POR COLABORADOR"],
        [`Período: ${startPt} até ${endPt}`],
        [`Gerado em: ${format(new Date(), "dd/MM/yyyy HH:mm:ss")}`],
        [],
        [
          "Colaborador",
          "E-mail",
          "Campanhas",
          "Total Disparos",
          "Enviados",
          "Entregues",
          "Lidos / Confirmados",
          "Falhas",
          "Aguardando / Fila",
          "Taxa de Entrega (%)"
        ]
      ];

      collaboratorsList.forEach((colab) => {
        summarySheetData.push([
          colab.userName || "Sem Atribuição",
          colab.userEmail || "-",
          colab.totalCampaigns || 0,
          colab.totalShipping || 0,
          colab.totalSent || 0,
          colab.totalDelivered || 0,
          colab.totalConfirmed || 0,
          colab.totalFailed || 0,
          colab.totalPending || 0,
          `${colab.deliveryRate || 0}%`
        ]);
      });

      // Linha de total geral
      summarySheetData.push([]);
      summarySheetData.push([
        "TOTAL GERAL",
        "-",
        totals.totalCampaigns || 0,
        totals.totalShipping || 0,
        totals.totalSent || 0,
        totals.totalDelivered || 0,
        totals.totalConfirmed || 0,
        totals.totalFailed || 0,
        totals.totalPending || 0,
        `${totals.deliveryRate || 0}%`
      ]);

      const wsSummary = XLSX.utils.aoa_to_sheet(summarySheetData);
      XLSX.utils.book_append_sheet(wb, wsSummary, "Resumo Colaboradores");

      // Aba 2: Disparos Detalhados
      const detailSheetData = [
        ["DISPAROS DETALHADOS"],
        [`Período: ${startPt} até ${endPt}`],
        [],
        [
          "ID",
          "Data Envio/Criação",
          "Campanha",
          "Template Meta",
          "Colaborador",
          "Destinatário",
          "Telefone",
          "Status",
          "Entregue em",
          "Lido em",
          "Falha em",
          "Motivo do Erro"
        ]
      ];

      const getStatusLabel = (st) => {
        switch (st) {
          case "delivered": return "Entregue";
          case "confirmed": return "Lido / Confirmado";
          case "sent": return "Enviado";
          case "failed": return "Falhou";
          default: return "Aguardando";
        }
      };

      (detailData.rows || []).forEach((row) => {
        detailSheetData.push([
          row.id,
          row.createdAt ? format(new Date(row.createdAt), "dd/MM/yyyy HH:mm:ss") : "-",
          row.campaignName || "-",
          row.templateName || "Não (Texto Livre)",
          row.userName || "Sem Atribuição",
          row.contactName || "-",
          row.number || "-",
          getStatusLabel(row.status),
          row.deliveredAt ? format(new Date(row.deliveredAt), "dd/MM/yyyy HH:mm:ss") : "-",
          row.confirmedAt ? format(new Date(row.confirmedAt), "dd/MM/yyyy HH:mm:ss") : "-",
          row.failedAt ? format(new Date(row.failedAt), "dd/MM/yyyy HH:mm:ss") : "-",
          row.errorMessage || "-"
        ]);
      });

      const wsDetail = XLSX.utils.aoa_to_sheet(detailSheetData);
      XLSX.utils.book_append_sheet(wb, wsDetail, "Disparos Detalhados");

      const fileName = `relatorio_disparos_${startPt.replace(/\//g, "-")}_a_${endPt.replace(/\//g, "-")}.xlsx`;
      XLSX.writeFile(wb, fileName);
      toast.success("Relatório Excel baixado com sucesso!");
    } catch (err) {
      toastError(err);
    } finally {
      setExporting(false);
    }
  };

  const renderStatusChip = (status) => {
    switch (status) {
      case "delivered":
        return (
          <Chip
            size="small"
            icon={<CheckCheck size={14} style={{ color: "#16A34A" }} />}
            label="Entregue"
            style={{ backgroundColor: "rgba(22, 163, 74, 0.12)", color: "#16A34A", fontWeight: 600 }}
          />
        );
      case "confirmed":
        return (
          <Chip
            size="small"
            icon={<Eye size={14} style={{ color: "#9333EA" }} />}
            label="Lido"
            style={{ backgroundColor: "rgba(147, 51, 234, 0.12)", color: "#9333EA", fontWeight: 600 }}
          />
        );
      case "sent":
        return (
          <Chip
            size="small"
            icon={<Send size={14} style={{ color: "#2563EB" }} />}
            label="Enviado"
            style={{ backgroundColor: "rgba(37, 99, 235, 0.12)", color: "#2563EB", fontWeight: 600 }}
          />
        );
      case "failed":
        return (
          <Chip
            size="small"
            icon={<XCircle size={14} style={{ color: "#DC2626" }} />}
            label="Falhou"
            style={{ backgroundColor: "rgba(220, 38, 38, 0.12)", color: "#DC2626", fontWeight: 600 }}
          />
        );
      default:
        return (
          <Chip
            size="small"
            icon={<Clock size={14} style={{ color: "#D97706" }} />}
            label="Aguardando"
            style={{ backgroundColor: "rgba(217, 119, 6, 0.12)", color: "#D97706", fontWeight: 600 }}
          />
        );
    }
  };

  return (
    <MainContainer>
      <MainHeader>
        <Grid container alignItems="center" justify="space-between">
          <Grid item>
            <Title>Relatório de Disparos em Massa</Title>
            <Typography variant="body2" color="textSecondary">
              Métricas e histórico detalhado de mensagens disparadas por colaborador
            </Typography>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              className={classes.exportButton}
              startIcon={exporting ? <CircularProgress size={18} color="inherit" /> : <FileSpreadsheet size={18} />}
              onClick={handleExportExcel}
              disabled={exporting || loading}
            >
              {exporting ? "Gerando Excel..." : "Exportar Excel (.xlsx)"}
            </Button>
          </Grid>
        </Grid>
      </MainHeader>

      <Paper className={classes.mainPaper}>
        {/* FILTROS */}
        <Paper elevation={0} className={classes.filterPaper}>
          <form onSubmit={handleFilter}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={3} md={2}>
                <TextField
                  fullWidth
                  label="Data Início"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                  variant="outlined"
                />
              </Grid>

              <Grid item xs={12} sm={3} md={2}>
                <TextField
                  fullWidth
                  label="Data Fim"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                  variant="outlined"
                />
              </Grid>

              {isAdmin && (
                <Grid item xs={12} sm={3} md={2}>
                  <FormControl fullWidth size="small" variant="outlined">
                    <InputLabel id="user-filter-label">Colaborador</InputLabel>
                    <Select
                      labelId="user-filter-label"
                      value={selectedUserId}
                      onChange={(e) => setSelectedUserId(e.target.value)}
                      label="Colaborador"
                    >
                      <MenuItem value="">
                        <em>Todos os Colaboradores</em>
                      </MenuItem>
                      {usersDropdown.map((u) => (
                        <MenuItem key={u.id} value={String(u.id)}>
                          {u.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}

              <Grid item xs={12} sm={3} md={2}>
                <FormControl fullWidth size="small" variant="outlined">
                  <InputLabel id="status-filter-label">Status do Envio</InputLabel>
                  <Select
                    labelId="status-filter-label"
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                    label="Status do Envio"
                  >
                    <MenuItem value="">Todos os Status</MenuItem>
                    <MenuItem value="delivered">Entregues</MenuItem>
                    <MenuItem value="confirmed">Lidos / Confirmados</MenuItem>
                    <MenuItem value="sent">Apenas Enviados</MenuItem>
                    <MenuItem value="failed">Falhas</MenuItem>
                    <MenuItem value="pending">Aguardando / Fila</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={4} md={2}>
                <TextField
                  fullWidth
                  label="Buscar"
                  placeholder="Nome, número ou campanha..."
                  value={searchParam}
                  onChange={(e) => setSearchParam(e.target.value)}
                  size="small"
                  variant="outlined"
                />
              </Grid>

              <Grid item xs={12} sm={8} md={2} style={{ display: "flex", gap: 8 }}>
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  fullWidth
                  disabled={loading}
                  startIcon={<Search size={16} />}
                >
                  Filtrar
                </Button>
                <Tooltip title="Limpar Filtros">
                  <IconButton onClick={handleClearFilters} size="small">
                    <RotateCcw size={18} />
                  </IconButton>
                </Tooltip>
              </Grid>
            </Grid>
          </form>
        </Paper>

        {/* CARDS DE INDICADORES (KPIS) */}
        <KpiCards totals={totals} />

        {/* ABAS */}
        <Box className={classes.tabsContainer}>
          <Tabs
            value={activeTab}
            onChange={(e, val) => setActiveTab(val)}
            indicatorColor="primary"
            textColor="primary"
          >
            <Tab label="Desempenho por Colaborador" icon={<Users size={16} />} />
            <Tab label={`Disparos Detalhados (${detailCount.toLocaleString("pt-BR")})`} icon={<Send size={16} />} />
          </Tabs>
        </Box>

        {loading && <LinearProgress style={{ marginTop: 8 }} />}

        {/* ABA 0: VISÃO POR COLABORADOR */}
        {activeTab === 0 && (
          <TableContainer className={classes.tableContainer}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell className={classes.tableHeader}>Colaborador</TableCell>
                  <TableCell className={classes.tableHeader} align="center">Campanhas</TableCell>
                  <TableCell className={classes.tableHeader} align="center">Total Disparos</TableCell>
                  <TableCell className={classes.tableHeader} align="center">Enviados</TableCell>
                  <TableCell className={classes.tableHeader} align="center">Entregues</TableCell>
                  <TableCell className={classes.tableHeader} align="center">Lidos</TableCell>
                  <TableCell className={classes.tableHeader} align="center">Falhas</TableCell>
                  <TableCell className={classes.tableHeader} align="center">Aguardando</TableCell>
                  <TableCell className={classes.tableHeader} align="center">Taxa de Entrega</TableCell>
                  <TableCell className={classes.tableHeader} align="center">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {collaboratorsList.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center" style={{ padding: 32 }}>
                      <Typography variant="body2" color="textSecondary">
                        Nenhum disparo encontrado para o período selecionado.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  collaboratorsList.map((colab, idx) => (
                    <TableRow key={colab.userId || idx} hover>
                      <TableCell>
                        <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                          {colab.userName}
                        </Typography>
                        {colab.userEmail && (
                          <Typography variant="caption" color="textSecondary">
                            {colab.userEmail}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Chip size="small" label={colab.totalCampaigns} />
                      </TableCell>
                      <TableCell align="center" style={{ fontWeight: 600 }}>
                        {colab.totalShipping?.toLocaleString("pt-BR")}
                      </TableCell>
                      <TableCell align="center" style={{ color: "#2563EB", fontWeight: 600 }}>
                        {colab.totalSent?.toLocaleString("pt-BR")}
                      </TableCell>
                      <TableCell align="center" style={{ color: "#16A34A", fontWeight: 600 }}>
                        {colab.totalDelivered?.toLocaleString("pt-BR")}
                      </TableCell>
                      <TableCell align="center" style={{ color: "#9333EA", fontWeight: 600 }}>
                        {colab.totalConfirmed?.toLocaleString("pt-BR")}
                      </TableCell>
                      <TableCell align="center" style={{ color: "#DC2626", fontWeight: 600 }}>
                        {colab.totalFailed?.toLocaleString("pt-BR")}
                      </TableCell>
                      <TableCell align="center" style={{ color: "#D97706" }}>
                        {colab.totalPending?.toLocaleString("pt-BR")}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          size="small"
                          label={`${colab.deliveryRate}%`}
                          style={{
                            backgroundColor: colab.deliveryRate >= 80 ? "rgba(22, 163, 74, 0.12)" : "rgba(217, 119, 6, 0.12)",
                            color: colab.deliveryRate >= 80 ? "#16A34A" : "#D97706",
                            fontWeight: 700
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Ver disparos deste colaborador">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => {
                              setSelectedUserId(colab.userId ? String(colab.userId) : "");
                              setActiveTab(1);
                            }}
                          >
                            <Eye size={18} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* ABA 1: DISPAROS DETALHADOS */}
        {activeTab === 1 && (
          <>
            <TableContainer className={classes.tableContainer}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell className={classes.tableHeader}>Data/Hora</TableCell>
                    <TableCell className={classes.tableHeader}>Campanha</TableCell>
                    <TableCell className={classes.tableHeader}>Colaborador</TableCell>
                    <TableCell className={classes.tableHeader}>Destinatário</TableCell>
                    <TableCell className={classes.tableHeader}>Telefone</TableCell>
                    <TableCell className={classes.tableHeader} align="center">Status</TableCell>
                    <TableCell className={classes.tableHeader}>Detalhes / Erro</TableCell>
                    <TableCell className={classes.tableHeader} align="center">Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {detailRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" style={{ padding: 32 }}>
                        <Typography variant="body2" color="textSecondary">
                          Nenhum registro de disparo encontrado com os filtros aplicados.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    detailRows.map((row) => (
                      <TableRow key={row.id} hover>
                        <TableCell>
                          <Typography variant="body2">
                            {row.createdAt ? format(new Date(row.createdAt), "dd/MM/yyyy HH:mm") : "-"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" style={{ fontWeight: 500 }}>
                            {row.campaignName}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {row.userName}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {row.contactName || "—"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" style={{ fontFamily: "monospace" }}>
                            {row.number}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          {renderStatusChip(row.status)}
                        </TableCell>
                        <TableCell>
                          {row.errorMessage ? (
                            <Tooltip title={row.errorMessage}>
                              <Typography variant="caption" style={{ color: "#DC2626", maxWidth: 220, display: "block", textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap" }}>
                                {row.errorMessage}
                              </Typography>
                            </Tooltip>
                          ) : (
                            <Typography variant="caption" color="textSecondary">
                              {row.deliveredAt ? `Entregue às ${format(new Date(row.deliveredAt), "HH:mm:ss")}` : (row.sentAt ? `Enviado às ${format(new Date(row.sentAt), "HH:mm:ss")}` : "—")}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Visualizar Detalhes do Disparo">
                            <IconButton
                              size="small"
                              onClick={() => setPreviewItem(row)}
                            >
                              <Eye size={18} />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              component="div"
              count={detailCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onChangePage={handleChangePage}
              onChangeRowsPerPage={handleChangeRowsPerPage}
              labelRowsPerPage="Linhas por página:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`}
            />
          </>
        )}
      </Paper>

      {/* MODAL DE PREVIEW DO DISPARO */}
      <Dialog
        open={Boolean(previewItem)}
        onClose={() => setPreviewItem(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Detalhes do Disparo #{previewItem?.id}
        </DialogTitle>
        <DialogContent dividers>
          {previewItem && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="textSecondary">Campanha:</Typography>
                <Typography variant="subtitle2">{previewItem.campaignName}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="textSecondary">Colaborador:</Typography>
                <Typography variant="subtitle2">{previewItem.userName}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="textSecondary">Destinatário:</Typography>
                <Typography variant="subtitle2">{previewItem.contactName || "—"}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="textSecondary">Telefone:</Typography>
                <Typography variant="subtitle2">{previewItem.number}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="textSecondary">Status Atual:</Typography>
                <Box mt={0.5}>{renderStatusChip(previewItem.status)}</Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="textSecondary">Data de Registro:</Typography>
                <Typography variant="subtitle2">
                  {previewItem.createdAt ? format(new Date(previewItem.createdAt), "dd/MM/yyyy HH:mm:ss") : "—"}
                </Typography>
              </Grid>

              {previewItem.templateName && (
                <Grid item xs={12}>
                  <Chip
                    size="small"
                    label={`Template Meta: ${previewItem.templateName}`}
                    style={{ backgroundColor: "rgba(37, 99, 235, 0.12)", color: "#2563EB", fontWeight: 600 }}
                  />
                </Grid>
              )}

              {previewItem.errorMessage && (
                <Grid item xs={12}>
                  <Typography variant="caption" style={{ color: "#DC2626", fontWeight: 600 }}>
                    Mensagem de Falha / Erro:
                  </Typography>
                  <Typography variant="body2" style={{ color: "#DC2626", backgroundColor: "rgba(220, 38, 38, 0.08)", padding: 8, borderRadius: 6, marginTop: 4 }}>
                    {previewItem.errorMessage}
                  </Typography>
                </Grid>
              )}

              <Grid item xs={12}>
                <Typography variant="caption" color="textSecondary">Conteúdo da Mensagem Disparada:</Typography>
                <Box className={classes.dialogMessage}>
                  {previewItem.message || "—"}
                </Box>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewItem(null)} color="primary">
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </MainContainer>
  );
};

export default CampaignReports;

